import { Button, Flex, Text } from "@radix-ui/themes";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckoutResponse } from "../../api/hitl-api";
import { useEffect, useRef, useState } from "react";
import { Sidebar } from "./sidebar";
import { ProductsTable } from "../../components/products-table";
import { CheckoutStatus, MessageCodes } from "./checkout-status";
import { setMessageContent } from "../../store/slices/modalsSlice";
import { useAppDispatch, useAppSelector } from "../../store";
import { PaymentModal } from "../../components/payment/payment-modal";
import { ErrorInstructionsModal } from "../../components/error-instructions";
import { useTranslation } from "react-i18next";
import { useUpdateEffect } from "react-use";
import { createNextState } from "@reduxjs/toolkit";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";
import { useLazyDetailedVariantsQuery, useZoneCartQuery } from "../../api/pos-api";
import { AutonomousPaymentProvider } from "../../components/payment/payment-context";
import { InitialPendingState } from "./initial-pending-state";
import { useCheckout } from "../../components/checkout/checkout-context";
import { CustomerHelp } from "./customer-help";
import toast from "react-hot-toast";
import { AxiosError } from "axios";

export function Checkout() {
  const { zoneId = "" } = useParams();
  const { t } = useTranslation();
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const messageContent = useAppSelector((state) => state.modals.messageContent);
  const dispatch = useAppDispatch();
  const [isEditable, setIsEditable] = useState(false);
  const [editedAt, setEditedAt] = useState<Date | null>(null);
  const [searchParams] = useSearchParams();
  const checkoutDateTime = searchParams.get("checkout_datetime");
  const [isCompleted, setIsCompleted] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const zoneCart = useZoneCartQuery(
    {
      checkout_datetime: checkoutDateTime!,
      zoneId,
    },
    {
      skip: !checkoutDateTime,
      pollingInterval: pollingEnabled ? 500 : 0,
    }
  );

  const [checkoutData, setCheckoutData] = useState<CheckoutResponse["result"]>();

  useUpdateEffect(() => {
    if (!zoneCart.data || !zoneCart.data.result.cart) return;

    setCheckoutData({
      ...zoneCart.data.result.cart,
      id: crypto.randomUUID(),
    });
  }, [zoneCart.data]);

  useEffect(() => {
    if (zoneCart.isError && (zoneCart.error as AxiosError)?.status === 400) {
      // navigate(getMainUrl({ zoneId, mode: "autonomous" }));
    }
  }, [zoneCart.isError]);

  useEffect(() => {
    return () => {
      dispatch(setMessageContent(null));
    };
  }, []);

  const lastPercentage = useRef({ value: 0, counter: 0 });

  useUpdateEffect(() => {
    if (zoneCart.data && zoneCart.data.result) {
      const { value, counter } = lastPercentage.current;
      const { progress_percentage } = zoneCart.data.result;

      if (progress_percentage === 100) {
        setIsCompleted(true);
        return;
      }

      if (value !== progress_percentage) {
        lastPercentage.current.value = progress_percentage;
        lastPercentage.current.counter = 1;
      } else {
        lastPercentage.current.counter++;

        if (counter === 10) {
          setIsCompleted(true);
        }
      }
    }
  }, [zoneCart]);

  const { cancelSession } = useCheckout();

  const isErrorDialogOpen =
    !!messageContent &&
    messageContent.message_codes.length > 0 &&
    messageContent.message_codes[0] !== MessageCodes.EvaluationCompleted;

  const productQuantityUpdater =
    (newQuantity: number, index: number) => (draft: CheckoutResponse["result"] | undefined) => {
      if (!draft) return draft;

      const targetLine = draft.lines[index];
      const sign = newQuantity - targetLine.quantity; // -1 or 1

      draft.lines[index].quantity = newQuantity;

      draft.lines[index].price_before_tax.amount = newQuantity * targetLine.variant.price_before_tax.amount;
      draft.lines[index].price_with_tax.amount = newQuantity * targetLine.variant.price_with_tax.amount;
      draft.lines[index].tax_price.amount = newQuantity * targetLine.variant.tax_price.amount;

      draft.price_before_tax.amount += sign * targetLine.variant.price_before_tax.amount;
      draft.price_with_tax.amount += sign * targetLine.variant.price_with_tax.amount;
      draft.tax_price.amount += sign * targetLine.variant.tax_price.amount;
    };

  const onUpdateQuantity = (newQuantity: number, index: number) => {
    setCheckoutData(createNextState(productQuantityUpdater(newQuantity, index)));
  };

  const onDelete = (index: number) => {
    setCheckoutData(
      createNextState((draft) => {
        if (!draft) return draft;

        const targetLine = draft.lines[index];

        draft.price_before_tax.amount -= targetLine.price_before_tax.amount;
        draft.price_with_tax.amount -= targetLine.price_with_tax.amount;
        draft.tax_price.amount -= targetLine.tax_price.amount;

        draft.lines.splice(index, 1);
      })
    );
  };

  const onCustomerSupport = () => {
    CustomerHelp.call({
      onScanned: () => {
        setIsEditable(true);
      },
    });
  };

  const [getDetailedVariants] = useLazyDetailedVariantsQuery();

  const onBarcodeScanned = async (value: string) => {
    const { data } = await getDetailedVariants(
      {
        sku: value,
        page: 1,
        page_size: 10,
      },
      true
    );

    if (data && data.results.length === 0) {
      return toast.error("Product not found. Please, try another");
    }

    if (data && data.results.length > 0) {
      const [variant] = data.results;

      setCheckoutData(
        createNextState((draft) => {
          if (!draft) return draft;

          const index = draft.lines.findIndex((line) => line.variant.id === variant.id);

          if (index > -1) {
            const updateQuantity = productQuantityUpdater(draft.lines[index].quantity + 1, index);

            updateQuantity(draft);
            draft.lines.unshift(draft.lines.splice(index, 1)[0]);
          } else {
            draft.lines.unshift({
              variant,
              quantity: 0,
              price_with_tax: {
                amount: variant.price_with_tax.amount,
                currency: variant.price_with_tax.currency,
              },
              price_before_tax: {
                amount: variant.price_before_tax.amount,
                currency: variant.price_before_tax.currency,
              },
              tax_price: {
                amount: variant.tax_price.amount,
                currency: variant.tax_price.currency,
              },
            });
            const updateQuantity = productQuantityUpdater(1, 0);
            updateQuantity(draft);
          }
        })
      );
    }
  };

  const getManualLines = () => {
    if (!editedAt || !checkoutData) return null;

    return checkoutData.lines.map((line) => ({
      variant_id: line.variant.id,
      quantity: line.quantity,
    }));
  };

  return (
    <div className="max-h-screen h-screen w-full bg-tezzro-bg">
      {!isReady && (
        <InitialPendingState
          checkoutDateTime={checkoutDateTime!}
          totalDuration={
            zoneCart.data
              ? zoneCart.data.result.checkout_end_retrival_wait_duration +
                zoneCart.data.result.track_finding_wait_duration
              : 0
          }
          onTimerComplete={() => {
            setIsReady(true);
          }}
          onCancel={cancelSession}
        />
      )}
      {checkoutData && zoneCart.data && isReady && (
        <AutonomousPaymentProvider
          manualLines={getManualLines()}
          cart={zoneCart.data.result.cart}
          trackCarts={zoneCart.data.result.track_carts}
        >
          <div className="flex flex-col h-full p-8 gap-3">
            <header className="flex justify-between p-4 bg-white border border-card-border rounded-2xl">
              <Text size="8" weight="medium" color="gray" highContrast>
                {!isEditable ? t("cardDetails") : t("manulaMode")}
              </Text>
              <Flex gap="2">
                <Button color="indigo" size="3" variant="soft" onClick={onCustomerSupport}>
                  <QuestionMarkCircledIcon />
                  {t("customerSupport")}
                </Button>
                <Button color="red" size="3" variant="soft" onClick={cancelSession}>
                  {t("cancelCheckout")}
                </Button>
              </Flex>
            </header>
            <div className="flex flex-grow gap-3">
              <main className="relative flex-grow rounded-2xl bg-white border border-card-border">
                <CheckoutStatus
                  isCompleted={isCompleted}
                  progressPercentage={zoneCart.data?.result.progress_percentage || 0}
                />
                <ProductsTable
                  enableTimer={false}
                  lines={checkoutData?.lines}
                  isEditable={isEditable}
                  onComplete={() => {}}
                  onUpdateQuantity={onUpdateQuantity}
                  onDelete={onDelete}
                  onBarcodeScanned={onBarcodeScanned}
                />
              </main>
              <Sidebar
                isEditable={isEditable}
                isCompleted={isCompleted}
                data={checkoutData}
                cartsCount={zoneCart.data?.result.track_carts.length || 0}
                onFinishEditing={() => {
                  setIsEditable(false);
                  setEditedAt(new Date());
                }}
                onCancelEditing={() => {
                  if (zoneCart.data && zoneCart.data.result.cart) {
                    setCheckoutData({
                      ...zoneCart.data.result.cart,
                      id: crypto.randomUUID(),
                    });
                  }
                  setIsEditable(false);
                }}
              />
            </div>
            <PaymentModal totalAmount={checkoutData.price_with_tax} setPolling={setPollingEnabled} />
            <ErrorInstructionsModal
              open={isErrorDialogOpen}
              messageContent={messageContent}
              onCancel={cancelSession}
            />
          </div>
        </AutonomousPaymentProvider>
      )}
      <CustomerHelp.Root />
    </div>
  );
}
